import type { ScenarioStep } from '../contract';
import type { ScenarioScreenshot } from '../archive/scenarioArchiveBuilder';
import type { RecorderSettings } from '../settings/settingsContract';
import type { RecordedGrpcCall, RecordingSink } from './recordingSink';
import { ScreenshotCollector, isKeyScreenshotAction, type ScreenshotCapturer } from './screenshotCapture';

/**
 * Recording fidelity.
 * - `full`: every UI step with its context details, every gRPC call with inline
 *   payloads (or refs when externalized).
 * - `lightweight`: the UI sequence + gRPC metadata/refs only — step context
 *   details and inline gRPC payloads are dropped to keep the archive small.
 */
export type RecordingMode = 'full' | 'lightweight';

/** A captured step. Superset of the contract {@link ScenarioStep}: adds the
 * correlation ids of the gRPC calls a UI step triggered (the UI<->gRPC link). */
export interface RecordedStep extends ScenarioStep {
  correlationIds?: string[];
}

/** Ambient UI context tracked alongside the step sequence. */
export interface RecorderContext {
  route?: string;
  activeTab?: string;
  openPanels?: string[];
  filters?: Record<string, unknown>;
  sort?: string;
  search?: string;
  [key: string]: unknown;
}

/** Immutable view of the current recording. */
export interface RecorderSnapshot {
  isRecording: boolean;
  mode: RecordingMode;
  steps: RecordedStep[];
  grpc: RecordedGrpcCall[];
  artifacts: Record<string, string>;
  context: RecorderContext;
  /** Notes about data that could not be collected — surfaced into scenario.json. */
  warnings: string[];
  /**
   * Collected `settings/` artifacts (web-settings, feature-flags, environment),
   * stashed here so the archive builder picks them up by their relative refs.
   * Optional: a snapshot taken before any settings were captured omits it.
   */
  settings?: RecorderSettings;
  /**
   * Key-step + final-state PNGs captured during a Full recording (card G). The
   * archive builder writes them under `screenshots/`. Present only when a
   * {@link ScreenshotCapturer} is attached; always empty in Lightweight.
   */
  screenshots?: ScenarioScreenshot[];
  /**
   * Step-index → screenshot ref map (card G); each entry stamps the matching
   * `step.screenshotRef` in scenario.json. Present only when a capturer is attached.
   */
  screenshotRefs?: Record<number, string>;
}

/** Input for a generic UI step capture. */
export interface UiStepInput {
  action: string;
  target?: string;
  details?: Record<string, unknown>;
}

/**
 * In-memory ordered recorder for a single scenario session. Framework-light and
 * unit-testable without rendering. Implements {@link RecordingSink} so the
 * gRPC-recorder interceptor pushes calls straight into the same ordered timeline
 * as the UI steps (they share one monotonic step counter).
 *
 * The Start/Stop UI, archive assembly and Revit collection are separate cards.
 */
export class ScenarioRecorder implements RecordingSink {
  private recording = false;
  private currentMode: RecordingMode = 'full';
  private steps: RecordedStep[] = [];
  private grpcCalls: RecordedGrpcCall[] = [];
  private artifacts: Record<string, string> = {};
  private context: RecorderContext = {};
  private counter = 0;
  private lastUiIndex: number | null = null;
  private warnings: string[] = [];
  private capturedSettings: RecorderSettings = {};
  /** Key-step + final-state screenshot collector; null until a capturer is attached. */
  private screenshots: ScreenshotCollector | null = null;
  /** In-flight best-effort step captures, drained by {@link flushScreenshots}. */
  private pendingScreenshots: Promise<unknown>[] = [];

  /**
   * Attach (or detach with `null`) the screenshot capture adapter. Called once
   * by the composition root ({@link ../recording/recordingStore}) with the live
   * {@link Html2CanvasCapturer}; tests inject a fake. Keeping the infra adapter
   * out of this module preserves the recording → infra boundary. Capture
   * failures are funneled into {@link recordWarning} (best-effort, card E).
   */
  setScreenshotCapturer(capturer: ScreenshotCapturer | null): void {
    this.screenshots = capturer
      ? new ScreenshotCollector(capturer, this.currentMode, { onWarning: (m) => this.recordWarning(m) })
      : null;
    this.pendingScreenshots = [];
  }

  // ---- lifecycle -----------------------------------------------------------

  /** Begin a fresh recording. No-op if already recording (idempotent). */
  start(mode: RecordingMode = 'full'): void {
    if (this.recording) {
      return;
    }
    this.recording = true;
    this.currentMode = mode;
    this.steps = [];
    this.grpcCalls = [];
    this.artifacts = {};
    this.context = {};
    this.counter = 0;
    this.lastUiIndex = null;
    this.warnings = [];
    this.capturedSettings = {};
    this.screenshots?.setMode(mode);
    this.screenshots?.reset();
    this.pendingScreenshots = [];
  }

  /** Stop recording, keeping the buffer for read/export. No-op if not recording. */
  stop(): void {
    if (!this.recording) {
      return;
    }
    this.recording = false;
  }

  /** Clear everything and stop. */
  reset(): void {
    this.recording = false;
    this.currentMode = 'full';
    this.steps = [];
    this.grpcCalls = [];
    this.artifacts = {};
    this.context = {};
    this.counter = 0;
    this.lastUiIndex = null;
    this.warnings = [];
    this.capturedSettings = {};
    this.screenshots?.setMode('full');
    this.screenshots?.reset();
    this.pendingScreenshots = [];
  }

  get mode(): RecordingMode {
    return this.currentMode;
  }

  setMode(mode: RecordingMode): void {
    this.currentMode = mode;
    this.screenshots?.setMode(mode);
  }

  // ---- RecordingSink (gRPC recorder pushes here) ---------------------------

  isRecording(): boolean {
    return this.recording;
  }

  reserveStepIndex(): number {
    const index = this.counter;
    this.counter += 1;
    return index;
  }

  pushGrpcCall(call: RecordedGrpcCall, artifacts: Record<string, string>): void {
    if (!this.recording) {
      return;
    }

    const stored = this.currentMode === 'lightweight' ? stripInlinePayloads(call) : call;
    this.grpcCalls.push(stored);
    Object.assign(this.artifacts, artifacts);

    const step: RecordedStep = {
      index: call.index,
      type: 'grpc',
      action: call.method,
      target: call.correlationId,
      timestamp: call.startedAt,
    };
    if (this.currentMode === 'full') {
      step.details = {
        correlationId: call.correlationId,
        status: call.status,
        durationMs: call.durationMs,
      };
    }
    this.steps.push(step);

    // Link this call to the UI step that triggered it (the most recent one).
    if (this.lastUiIndex !== null) {
      const ui = this.steps.find((s) => s.index === this.lastUiIndex && s.type === 'ui');
      if (ui) {
        (ui.correlationIds ??= []).push(call.correlationId);
      }
    }
  }

  // ---- UI capture ----------------------------------------------------------

  /** Capture a generic UI step. Returns its step index, or null when idle. */
  recordUiStep(input: UiStepInput): number | null {
    if (!this.recording) {
      return null;
    }

    const index = this.reserveStepIndex();
    const step: RecordedStep = {
      index,
      type: 'ui',
      action: input.action,
      timestamp: new Date().toISOString(),
    };
    if (input.target !== undefined) {
      step.target = input.target;
    }
    if (this.currentMode === 'full') {
      const details: Record<string, unknown> = { ...(input.details ?? {}) };
      if (Object.keys(this.context).length > 0) {
        details.context = { ...this.context };
      }
      if (Object.keys(details).length > 0) {
        step.details = details;
      }
    }
    this.steps.push(step);
    this.lastUiIndex = index;
    this.scheduleStepScreenshot(index, step.action);
    return index;
  }

  recordClick(target: string, details?: Record<string, unknown>): number | null {
    return this.recordUiStep({ action: 'click', target, details });
  }

  recordInputChange(target: string, value: unknown): number | null {
    return this.recordUiStep({ action: 'input.change', target, details: { value } });
  }

  recordSelection(target: string, selection: unknown): number | null {
    return this.recordUiStep({ action: 'select', target, details: { selection } });
  }

  recordPanelToggle(target: string, open: boolean): number | null {
    return this.recordUiStep({ action: open ? 'panel.open' : 'panel.close', target });
  }

  recordCommand(command: string, details?: Record<string, unknown>): number | null {
    return this.recordUiStep({ action: `command.${command}`, details });
  }

  recordNavigation(route: string): number | null {
    this.updateContext({ route });
    return this.recordUiStep({ action: 'navigate', target: route });
  }

  recordToast(message: string, severity?: string): number | null {
    return this.recordUiStep({ action: 'toast', target: severity, details: { message } });
  }

  recordUiError(message: string): number | null {
    return this.recordUiStep({ action: 'ui.error', details: { message } });
  }

  recordActionResult(action: string, result: unknown): number | null {
    return this.recordUiStep({ action: 'action.result', target: action, details: { result } });
  }

  /** Merge ambient UI context (route, active tab, open panels, filters, ...). */
  updateContext(partial: Partial<RecorderContext>): void {
    if (!this.recording) {
      return;
    }
    this.context = { ...this.context, ...partial };
  }

  /**
   * Record a "could not collect" note. Unlike step capture this is accepted even
   * after stop (collection / archive-build warnings fire around teardown) so the
   * gap reaches scenario.json. Cleared on start()/reset().
   */
  recordWarning(message: string): void {
    if (message && message.length > 0) {
      this.warnings.push(message);
    }
  }

  /**
   * Stash collected `settings/` artifacts into the buffer. Like
   * {@link recordWarning}, accepted even after stop because settings are
   * gathered around stop/export time. Merges so the three artifacts can arrive
   * separately. Cleared on start()/reset().
   */
  captureSettings(settings: RecorderSettings): void {
    if (!settings) {
      return;
    }
    this.capturedSettings = { ...this.capturedSettings, ...settings };
  }

  // ---- screenshots (card G) ------------------------------------------------

  /**
   * Fire-and-forget a key-step screenshot. The collector decides whether to
   * actually capture (Full mode + key action); non-key/Lightweight is a cheap
   * no-op so we only track promises that can produce a shot. Best-effort —
   * never throws into the recording path.
   */
  private scheduleStepScreenshot(index: number, action: string): void {
    if (this.screenshots && isKeyScreenshotAction(action)) {
      this.pendingScreenshots.push(this.screenshots.captureStep(index, action));
    }
  }

  /**
   * Capture the final-state screenshot (Full mode only). Best-effort and tracked
   * so {@link flushScreenshots} awaits it before the snapshot is read. Returns
   * the ref or null when skipped / no capturer / capture failed.
   */
  async captureFinalState(): Promise<string | null> {
    if (!this.screenshots) {
      return null;
    }
    const pending = this.screenshots.captureFinalState();
    this.pendingScreenshots.push(pending);
    return pending;
  }

  /** Await all in-flight best-effort captures so {@link getSnapshot} is complete. */
  async flushScreenshots(): Promise<void> {
    await Promise.allSettled(this.pendingScreenshots.splice(0));
  }

  // ---- selectors -----------------------------------------------------------

  /** The ordered step timeline (sorted by index — async gRPC pushes may arrive late). */
  getSteps(): RecordedStep[] {
    return [...this.steps].sort((a, b) => a.index - b.index);
  }

  getGrpcCalls(): RecordedGrpcCall[] {
    return [...this.grpcCalls].sort((a, b) => a.index - b.index);
  }

  getArtifacts(): Record<string, string> {
    return { ...this.artifacts };
  }

  getSnapshot(): RecorderSnapshot {
    const snapshot: RecorderSnapshot = {
      isRecording: this.recording,
      mode: this.currentMode,
      steps: this.getSteps(),
      grpc: this.getGrpcCalls(),
      artifacts: this.getArtifacts(),
      context: { ...this.context },
      warnings: [...this.warnings],
      settings: { ...this.capturedSettings },
    };
    // Only carry the screenshot fields when a capturer is attached, so a plain
    // recorder (or a Lightweight/no-capturer session) keeps a minimal snapshot.
    if (this.screenshots) {
      snapshot.screenshots = this.screenshots.getScreenshots();
      snapshot.screenshotRefs = this.screenshots.getStepRefs();
    }
    return snapshot;
  }
}

function stripInlinePayloads(call: RecordedGrpcCall): RecordedGrpcCall {
  const copy: RecordedGrpcCall = { ...call };
  delete copy.request;
  delete copy.response;
  return copy;
}

/** App-wide recorder singleton. Registered as the gRPC sink in recordingStore.ts. */
export const scenarioRecorder = new ScenarioRecorder();
